var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, nothing } from 'lit';
import { registerCustomElement } from '../utils/registerCustomElement';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { handleButtonTheme, styles } from './styles';
import { spreadProps } from '@open-wc/lit-helpers';
let Button = class Button extends LitElement {
    constructor() {
        super(...arguments);
        this.variant = 'primary';
        this.size = 'sm';
        this.as = 'button';
        this.hover = true;
        this.disabled = false;
        this.fullWidth = false;
        this.autoFocus = false;
        this.href = '';
        this.rel = '';
        this.id = '';
        // only values greater-equal to zero are valid
        this.tabIndex = -1;
        this.onClick = (e) => { };
        this.styles = handleButtonTheme(this.variant);
    }
    /*
      improve security for new windows
      when used as anchor tag
    */
    get handleRel() {
        if (this.target === '_blank') {
            return 'noreferrer noopener';
        }
        return undefined;
    }
    updated(changedProperties) {
        var _a, _b;
        if (changedProperties.has('autoFocus') && this.autoFocus) {
            (_b = (_a = this === null || this === void 0 ? void 0 : this.renderRoot) === null || _a === void 0 ? void 0 : _a.querySelector('button')) === null || _b === void 0 ? void 0 : _b.focus();
        }
    }
    getPropsToSpread() {
        return {
            href: this.href,
            target: this.target,
            rel: this.handleRel,
        };
    }
    render() {
        const buttonClasses = classMap({
            'is-full-width': this.fullWidth,
            'button--primary': this.variant === 'primary',
            'button--secondary': this.variant === 'secondary',
            'button--sm': this.size === 'sm',
            'button--md': this.size === 'md',
            'button--lg': this.size === 'lg',
        });
        const tabIndexValue = this.tabIndex >= 0 ? this.tabIndex : nothing;
        /*
          Button as an anchor tag cannot use 'disabled' attribute,
          instead we need to disable click event manually if disabled = true
        */
        const handleClick = (event) => {
            if (this.disabled) {
                event.preventDefault();
                event.stopPropagation();
            }
            else {
                this.onClick(event);
            }
        };
        /*
          @TODO:
          Condense this to use a shared template
          and make more DRY
        */
        if (this.as === 'button') {
            return html `
        <button
          part="button"
          tabindex=${tabIndexValue}
          ?autofocus=${this.autoFocus}
          type=${ifDefined(this.type)}
          class=${buttonClasses}
          data-disabled=${ifDefined(this.disabled ? true : undefined)}
          aria-disabled=${ifDefined(this.disabled ? true : undefined)}
          id=${ifDefined(this.id)}
          @click=${handleClick}
        >
          <slot></slot>
        </button>
      `;
        }
        else if (this.as === 'a') {
            return html `
        <a
          part="a"
          tabindex=${tabIndexValue}
          .autofocus=${this.autoFocus}
          type=${ifDefined(this.type)}
          class=${buttonClasses}
          id=${ifDefined(this.id)}
          data-disabled=${ifDefined(this.disabled ? true : undefined)}
          aria-disabled=${ifDefined(this.disabled ? true : undefined)}
          ${spreadProps(this.getPropsToSpread())}
          @click=${handleClick}
        >
          <slot></slot>
        </a>
      `;
        }
    }
};
Button.styles = styles;
__decorate([
    property({ type: String })
], Button.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "size", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "as", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "type", void 0);
__decorate([
    property({ type: Boolean })
], Button.prototype, "hover", void 0);
__decorate([
    property({ type: Boolean })
], Button.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], Button.prototype, "fullWidth", void 0);
__decorate([
    property({ type: Boolean })
], Button.prototype, "autoFocus", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "href", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "target", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "rel", void 0);
__decorate([
    property({ type: String })
], Button.prototype, "id", void 0);
__decorate([
    property({ type: Number })
], Button.prototype, "tabIndex", void 0);
__decorate([
    property({ type: Function })
], Button.prototype, "onClick", void 0);
__decorate([
    property({ attribute: false })
], Button.prototype, "styles", void 0);
Button = __decorate([
    registerCustomElement('grain-button')
], Button);
export { Button };
